import {
  Button,
  Input,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { Modal } from "../../components/molecules/Modal/Modal";
import { Pagination } from "../../components/molecules/Pagination";
import { featureApi } from "../../services/feature.api";
import {
  FeatureDto,
  FeatureLabelVM,
  FeaturePayloadVM,
} from "../../types/feature.definition";
import { textDirection } from "../../utils/text-direction-utils";

type LangKey = "en" | "fr" | "ar";

const LANGS: { key: LangKey; label: string }[] = [
  { key: "en", label: "English" },
  { key: "fr", label: "Français" },
  { key: "ar", label: "العربية" },
];

function emptyLabel(): FeatureLabelVM {
  return { en: "", fr: "", ar: "" };
}

export const FeatureManagement: React.FC = () => {
  const { t } = useTranslation();

  const [features, setFeatures] = useState<FeatureDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [lang, setLang] = useState<LangKey>("fr");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<FeatureDto | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [confirmToggleFeature, setConfirmToggleFeature] =
    useState<FeatureDto | null>(null);

  const [code, setCode] = useState("");
  const [label, setLabel] = useState<FeatureLabelVM>(emptyLabel());
  const [activeLang, setActiveLang] = useState<LangKey>("fr");
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);
  const editingIdRef = useRef<number | null>(null);

  const loadFeatures = (targetLang: LangKey) => {
    setLoading(true);
    featureApi
      .getAll(targetLang)
      .then(setFeatures)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadFeatures(lang);
  }, [lang]);

  const filtered = features.filter(
    f =>
      f.code.toLowerCase().includes(search.toLowerCase()) ||
      f.label.toLowerCase().includes(search.toLowerCase())
  );

  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const openCreateModal = () => {
    setEditingFeature(null);
    setCode("");
    setLabel(emptyLabel());
    setActiveLang("fr");
    setIsFormOpen(true);
  };

  const openEditModal = (feature: FeatureDto) => {
    editingIdRef.current = feature.id;
    setEditingFeature(feature);
    setCode(feature.code);
    setLabel({ en: feature.label, fr: feature.label, ar: feature.label });
    setActiveLang("fr");
    setIsFormOpen(true);

    const requestedId = feature.id;
    setIsLoadingTranslations(true);
    featureApi
      .getAllTranslations(requestedId)
      .then(translations => {
        if (editingIdRef.current === requestedId) {
          setLabel(translations);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (editingIdRef.current === requestedId) {
          setIsLoadingTranslations(false);
        }
      });
  };

  const closeFormModal = () => {
    editingIdRef.current = null;
    setIsFormOpen(false);
    setEditingFeature(null);
  };

  const handleSave = async () => {
    if (!code.trim()) return;
    setSaving(true);
    const payload: FeaturePayloadVM = {
      code: code.trim().toUpperCase(),
      label,
    };
    try {
      if (editingFeature) {
        await featureApi.update(editingFeature.id, payload);
      } else {
        await featureApi.create(payload);
      }
      closeFormModal();
      loadFeatures(lang);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleConfirm = async () => {
    if (!confirmToggleFeature) return;
    const feature = confirmToggleFeature;
    setConfirmToggleFeature(null);
    setTogglingId(feature.id);
    try {
      const updated = await featureApi.toggleEnabled(feature.id);
      setFeatures(prev =>
        prev.map(f =>
          f.id === updated.id ? { ...f, enabled: updated.enabled } : f
        )
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (confirmDeleteId === null) return;
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    setDeletingId(id);
    try {
      await featureApi.delete(id);
      loadFeatures(lang);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t("admin.featureMgmt.title")}
          </h2>
          <p className="text-neutral-600">{t("admin.featureMgmt.subtitle")}</p>
        </div>
        <div className="flex items-center space-x-2">
          {/* Language selector */}
          <div className="flex rounded-lg border border-neutral-200 overflow-hidden">
            {LANGS.map(({ key, label: langLabel }) => (
              <button
                key={key}
                onClick={() => {
                  setLang(key);
                  setCurrentPage(1);
                }}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  lang === key
                    ? "bg-ews-primary text-white"
                    : "bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                {langLabel}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => loadFeatures(lang)}
            disabled={loading}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span>{t("common.refresh")}</span>
          </Button>
          <Button
            onClick={openCreateModal}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t("admin.featureMgmt.addNew")}</span>
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="page-section pb-0">
        <div className="relative max-w-sm">
          <Input
            size="sm"
            placeholder={t("admin.featureMgmt.search")}
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="page-section">
        {loading ? (
          <div className="py-12 text-center">
            <RefreshCw className="mx-auto w-8 h-8 animate-spin text-neutral-400" />
            <p className="mt-2 text-sm text-neutral-500">
              {t("common.loading")}
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {search
                ? t("admin.featureMgmt.noResults")
                : t("admin.featureMgmt.empty")}
            </h3>
            {!search && (
              <div className="mt-6">
                <Button onClick={openCreateModal}>
                  <Plus className="mr-2 w-4 h-4" />
                  {t("admin.featureMgmt.addNew")}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-neutral-200">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-neutral-600 w-16">
                      {t("admin.featureMgmt.table.id")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-neutral-600 w-64">
                      {t("admin.featureMgmt.table.code")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-neutral-600">
                      {t("admin.featureMgmt.table.label")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-neutral-600 w-28">
                      {t("admin.featureMgmt.table.enabled")}
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-neutral-600 w-28">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {paginated.map(feature => (
                    <tr
                      key={feature.id}
                      className="hover:bg-neutral-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-neutral-500 font-mono text-xs">
                        #{feature.id}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">
                          {feature.code}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-neutral-700">
                        {feature.label || (
                          <span className="italic text-neutral-400">
                            {t("admin.featureMgmt.noLabel")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          role="switch"
                          aria-checked={feature.enabled}
                          disabled={togglingId === feature.id}
                          onClick={() => setConfirmToggleFeature(feature)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                            feature.enabled
                              ? "bg-ews-primary"
                              : "bg-neutral-300"
                          }`}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                              feature.enabled
                                ? "translate-x-4.5"
                                : "translate-x-0.5"
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end items-center space-x-1">
                          <button
                            onClick={() => openEditModal(feature)}
                            className="p-1.5 rounded text-neutral-600 hover:bg-neutral-100 hover:text-ews-primary transition-colors"
                            title={t("common.edit")}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(feature.id)}
                            disabled={deletingId === feature.id}
                            className="p-1.5 rounded text-neutral-600 hover:bg-danger-50 hover:text-danger-600 transition-colors disabled:opacity-50"
                            title={t("common.delete")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2 text-xs text-neutral-500 border-t border-neutral-100 bg-neutral-50">
                {t("admin.featureMgmt.count", { count: totalItems })}
              </div>
            </div>
            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={p => setCurrentPage(p)}
                  onItemsPerPageChange={s => {
                    setItemsPerPage(s);
                    setCurrentPage(1);
                  }}
                  showPageNumbers
                  maxVisiblePages={5}
                  showItemsPerPageSelector
                  itemsPerPageOptions={[10, 25, 50, 100]}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={isFormOpen}
        onClose={closeFormModal}
        size="md"
        title={
          editingFeature
            ? t("admin.featureMgmt.editTitle")
            : t("admin.featureMgmt.addNew")
        }
      >
        <div className="p-6 space-y-5">
          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t("admin.featureMgmt.form.code")}
            </label>
            <Input
              size="sm"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder={t("admin.featureMgmt.form.codePlaceholder")}
            />
            <p className="mt-1 text-xs text-neutral-400">
              {t("admin.featureMgmt.form.codeHint")}
            </p>
          </div>

          {/* Language tabs for label */}
          <div>
            <div className="flex items-center border-b border-neutral-200 mb-4">
              {LANGS.map(({ key, label: langLabel }) => (
                <button
                  key={key}
                  onClick={() => setActiveLang(key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    activeLang === key
                      ? "border-ews-primary text-ews-primary"
                      : "border-transparent text-neutral-600 hover:text-neutral-900"
                  }`}
                >
                  {langLabel}
                </button>
              ))}
              {isLoadingTranslations && (
                <span className="ml-auto flex items-center gap-1 text-xs text-neutral-400 pb-1">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  {t("common.loading")}
                </span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t("admin.featureMgmt.form.label")}
              </label>
              <Input
                size="sm"
                dir={textDirection(activeLang)}
                value={label[activeLang]}
                onChange={e =>
                  setLabel(prev => ({ ...prev, [activeLang]: e.target.value }))
                }
                placeholder={t("admin.featureMgmt.form.labelPlaceholder")}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-2 border-t border-neutral-200">
            <Button
              variant="outline"
              onClick={closeFormModal}
              disabled={saving}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSave}
              loading={saving}
              disabled={saving || !code.trim()}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t("admin.featureMgmt.deleteConfirm.title")}
        message={t("admin.featureMgmt.deleteConfirm.message")}
        variant="danger"
        isLoading={deletingId !== null}
        confirmLabel={t("common.delete")}
      />

      {/* Toggle Confirmation */}
      <ConfirmationModal
        isOpen={confirmToggleFeature !== null}
        onClose={() => setConfirmToggleFeature(null)}
        onConfirm={handleToggleConfirm}
        title={t(
          confirmToggleFeature?.enabled
            ? "admin.featureMgmt.toggleConfirm.disable.title"
            : "admin.featureMgmt.toggleConfirm.enable.title"
        )}
        message={t(
          confirmToggleFeature?.enabled
            ? "admin.featureMgmt.toggleConfirm.disable.message"
            : "admin.featureMgmt.toggleConfirm.enable.message"
        )}
        variant={confirmToggleFeature?.enabled ? "danger" : "success"}
        isLoading={togglingId !== null}
        confirmLabel={t(
          confirmToggleFeature?.enabled
            ? "admin.featureMgmt.toggleConfirm.disable.confirm"
            : "admin.featureMgmt.toggleConfirm.enable.confirm"
        )}
      />
    </div>
  );
};
